import {
  createContext,
  ReactNode,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import ReconnectingEventSource from 'reconnecting-eventsource';
import { del } from '@truefit/http-utils';

import useCognito from '../../authentication/hooks/useCognito';
import { useTimeApiData } from '@/shared/services/getTimeApiDate';
import { GetGlobalSSEUrl } from '@/util';
import { sessionDnsSuffix } from '@/shared/constants';
import useCreateSessionMutation from '@/features/authentication/hooks/useCreateSession';
import { AUTH_PATHS, getSignInUrl } from '@/features/authentication/functions/returnUrl';
import useCurrentCloudUser from '@/shared/hooks/user/getCurrentUser';
import type { CloudUser } from '@c360/types';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import CustomNotificationCenter from './CustomNotificationCenter';

const NO_PLAYER_PATHS = ['/mcp/authorize'];

// Types
export enum LocalSessionState {
  NOT_LOADED,
  LOADING,
  NO_LOCAL_SESSION,
  HAS_LOCAL_SESSION,
  AUTHENTICATED,
  READY,
}

export type ProgressMessage = {
  id: string;
  message: string;
  status: 'info' | 'success' | 'error';
  timestamp: number;
};

export const getSessionStateString = (state: LocalSessionState) => {
  const stateMap = {
    [LocalSessionState.NOT_LOADED]: 'NOT_LOADED',
    [LocalSessionState.LOADING]: 'LOADING',
    [LocalSessionState.NO_LOCAL_SESSION]: 'NO_LOCAL_SESSION',
    [LocalSessionState.HAS_LOCAL_SESSION]: 'HAS_LOCAL_SESSION',
    [LocalSessionState.AUTHENTICATED]: 'AUTHENTICATED',
    [LocalSessionState.READY]: 'READY',
  };

  return stateMap[state] || 'UNKNOWN';
};

// Signed in: enough for business-api calls, which only need the Cognito JWT.
// READY additionally means the cloud player is up — only the game pages need that.
export const isSignedIn = (state: LocalSessionState) =>
  state === LocalSessionState.AUTHENTICATED || state === LocalSessionState.READY;

// Context type definition
type AuthContextType = {
  sessionState: LocalSessionState;
  loading: boolean;
  sessionHandler: string;
  isExpiringSession: boolean;
  progressMessages: ProgressMessage[];
  currentUser?: CloudUser;
  currentUserStatus?: string;
  currentUserLoading: boolean;
  refetchCurrentUser: () => void;
  timezone: string;
  handleSignOut: (bypassSessionDeletion?: boolean) => Promise<void>;
  addProgressMessage: (message: string, status?: 'info' | 'success' | 'error') => void;
  setIsExpiringSession: (isExpiring: boolean) => void;
  getToken: () => string | undefined;
};

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  sessionState: LocalSessionState.NOT_LOADED,
  loading: true,
  sessionHandler: '',
  isExpiringSession: false,
  progressMessages: [],
  currentUser: undefined,
  currentUserStatus: undefined,
  currentUserLoading: false,
  refetchCurrentUser: () => {},
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  handleSignOut: async () => {},
  addProgressMessage: () => {},
  setIsExpiringSession: () => {},
  getToken: () => undefined,
});

// Provider component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const cognito = useCognito();
  const timeApiDataQuery = useTimeApiData();
  const interactiveSession = useCreateSessionMutation();
  const currentUserQuery = useCurrentCloudUser();
  const sse = useRef<ReconnectingEventSource | null>(null);
  const isPublicRoute = AUTH_PATHS.includes(window.location.pathname);
  // Signed-in pages that never need a cloud player. They skip the SSE below, whose
  // 'initialized' event is what deploys one (POST /session). The MCP consent page
  // is a stop on an OAuth redirect: the user leaves it within seconds.
  const needsPlayer = !isPublicRoute && !NO_PLAYER_PATHS.includes(window.location.pathname);

  // Ref to always have latest currentUserQuery data (avoids stale closures in SSE callbacks)
  const currentUserQueryRef = useRef(currentUserQuery);
  useEffect(() => {
    currentUserQueryRef.current = currentUserQuery;
  });

  // State declarations
  const [sessionState, setSessionState] = useState<LocalSessionState>(
    cognito.authenticated ? LocalSessionState.AUTHENTICATED : LocalSessionState.NOT_LOADED,
  );
  const [loading, setLoading] = useState(true);
  const [sessionHandler, setSessionHandler] = useState<string | null>(null);
  const [sessionHost, setSessionHost] = useState<string | null>(null);

  // Refs to track current values (avoid stale closures in SSE callbacks)
  const sessionHandlerRef = useRef<string | null>(null);
  const sessionStateRef = useRef<LocalSessionState>(sessionState);

  const [isExpiringSession, setIsExpiringSession] = useState(false);
  const [progressMessages, setProgressMessages] = useState<ProgressMessage[]>([]);

  const [cognitoSession, setCognitoSession] = useState<CognitoUserSession>();

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const currentUser = currentUserQuery.data?.data?.user as CloudUser | undefined;
  const timezone =
    currentUser?.tz && currentUser.tz !== 'UNSET' ? currentUser.tz : browserTimezone;

  // Session creation lock with timeout mechanism
  const [sessionLock, setSessionLock] = useState<{
    locked: boolean;
    timestamp: number | null;
  }>({ locked: false, timestamp: null });

  // Sync refs with state
  useEffect(() => {
    sessionHandlerRef.current = sessionHandler;
  }, [sessionHandler]);

  useEffect(() => {
    sessionStateRef.current = sessionState;
  }, [sessionState]);

  // Add progress message helper
  const addProgressMessage = useCallback(
    (message: string, status: 'info' | 'success' | 'error' = 'info') => {
      setProgressMessages((prev) => {
        // Mark previous info messages as success
        const updatedPrev = prev.map((msg) =>
          msg.status === 'info' ? { ...msg, status: 'success' as const } : msg,
        );

        // Add new message
        return [
          ...updatedPrev,
          {
            id: uuidv4(),
            message,
            status,
            timestamp: Date.now(),
          },
        ];
      });
    },
    [],
  );

  // Initialize with first message
  useEffect(() => {
    if (needsPlayer) {
      addProgressMessage('Initializing new CXVH player...');
    }
  }, [needsPlayer, addProgressMessage]);

  // Handle sign out process
  const handleSignOut = useCallback(
    async (bypassSessionDeletion = false) => {
      if (cognito.authenticated) {
        try {
          // Sign out from Cognito
          await new Promise<void>((resolve, reject) => {
            cognito.signOut({
              onSuccess: () => resolve(),
              onFailure: (error) => reject(error),
            });
          });

          // Delete session if not bypassed
          if (!bypassSessionDeletion) {
            await del('/v1.0/session');
          }
        } catch (error) {
          console.error('Error during sign-out process:', error);
        }

        // Close SSE connection
        if (sse.current) {
          sse.current.close();
          sse.current = null;
        }

        // Reset state
        setSessionState(LocalSessionState.NO_LOCAL_SESSION);
        setSessionHandler(null);
        setSessionHost(null);
        setLoading(false);
        setSessionLock({ locked: false, timestamp: null });
      }
    },
    [cognito],
  );

  // Process real-time SSE events
  const processRealtimeEvent = useCallback(
    async (data: any, sessionId: string) => {
      const { event, payload } = data;

      // Validate payload session ID matches current session
      if (payload?.sessionId && payload.sessionId !== sessionId) {
        return;
      }

      switch (event) {
        case 'initialized': {
          if (isPublicRoute) {
            break;
          }
          // Fetch time API data and create session with lock mechanism
          const now = Date.now();
          const LOCK_TIMEOUT_MS = 5000; // 5 second timeout

          // Use refs to get current values (avoid stale closures)
          const currentHandler = sessionHandlerRef.current;
          const currentState = sessionStateRef.current;

          // Check if a valid session already exists - no need to create a new one
          if (currentHandler && currentState === LocalSessionState.READY) {
            console.debug('Valid session already exists, skipping session creation');
            break;
          }

          // Check if session handler exists (session in progress or ready)
          if (currentHandler) {
            console.debug('Session already created, skipping duplicate');
            break;
          }

          // Check if lock is held and still valid
          if (sessionLock.locked && sessionLock.timestamp) {
            const lockAge = now - sessionLock.timestamp;
            if (lockAge < LOCK_TIMEOUT_MS) {
              break;
            } else {
              console.warn('Session lock expired, proceeding with new attempt');
            }
          }

          // Acquire lock
          setSessionLock({ locked: true, timestamp: now });

          console.debug('Current handler:', currentHandler);
          console.debug('Current state:', currentState);

          try {
            let orgId = currentUserQueryRef.current.data?.data?.org?.id;
            if (!orgId) {
              // User data may not be loaded yet — wait for it
              const userResult = await currentUserQueryRef.current.refetch();
              orgId = userResult.data?.data?.org?.id;
            }
            if (!orgId) {
              console.error('POST /session: no org in context, skipping session creation');
              setSessionLock({ locked: false, timestamp: null });
              break;
            }
            const timeData = await timeApiDataQuery.refetch();
            interactiveSession.mutate({ org: orgId, ...(timeData.data ?? {}) });
          } catch (error) {
            console.error('Error fetching time API data:', error);
            setSessionLock({ locked: false, timestamp: null });
          }
          break;
        }

        case 'session-deploy-error':
          addProgressMessage(payload.message || 'Cloud player deployment error', 'error');
          console.error('Session deployment error:', payload.message);
          break;

        case 'session-deploy-progress':
          addProgressMessage(payload.message || 'Deploying new cloud player', 'info');
          break;

        case 'session-ready':
          addProgressMessage('Cloud player ready', 'success');
          setSessionState(LocalSessionState.READY);
          setLoading(false);
          break;

        case 'session-handler':
          if (payload.handler) {
            setSessionHandler(payload.handler);
            // Sole place the session hostname is assembled — everything downstream
            // (websockets, the video player) consumes `sessionHost` as-is.
            setSessionHost(`${payload.handler}.${sessionDnsSuffix}`);
            // Release lock after successful session creation
            setSessionLock({ locked: false, timestamp: null });
          }
          break;

        case 'session-ended':
          //  addProgressMessage('Cloud player session ended', 'error');
          setSessionState(LocalSessionState.NO_LOCAL_SESSION);
          handleSignOut(true);
          break;

        case 'session-expiring':
          addProgressMessage('Session expiring soon', 'info');
          setIsExpiringSession(true);
          break;

        default:
          // Ignore unhandled event types
          break;
      }
    },
    [
      interactiveSession,
      timeApiDataQuery.data,
      addProgressMessage,
      handleSignOut,
      sessionLock,
      sessionHandler
    ],
  );

  // Update Cognito context when session handler changes
  useEffect(() => {
    console.log('[AuthContext] sessionHandler or cognitoSession changed:', {
      sessionHandler,
      sessionHost,
      cognitoSession,
    });
    
    if (sessionHandler && cognitoSession) {
      cognito.setHttpAuth(cognitoSession, sessionHandler, sessionHost ?? undefined);
      cognito.updateCognitoContext({ sessionHandler, sessionHost: sessionHost ?? '' });
    }
  }, [sessionHandler, sessionHost, cognitoSession, addProgressMessage]);

  // Initial session check
  useEffect(() => {
    if (sessionState === LocalSessionState.NOT_LOADED && cognito) {
      setSessionState(LocalSessionState.LOADING);

      cognito.getSession({
        onSuccess(userSession) {
          setSessionState(LocalSessionState.HAS_LOCAL_SESSION);

          setCognitoSession(userSession);
        },
        onFailure(error) {
          console.warn('No valid session found:', error);
          setSessionState(LocalSessionState.NO_LOCAL_SESSION);
          setLoading(false);
        },
      });
    }
  }, [cognito, sessionState, addProgressMessage]);

  useEffect(() => {
    if (cognito.authenticated) {
      if (cognito.userSession) {
        // Update cognitoSession whenever the Cognito context's userSession changes
        // This ensures SSE and other consumers get the refreshed token
        setCognitoSession(cognito.userSession);
        if (sessionState === LocalSessionState.NOT_LOADED ||
            sessionState === LocalSessionState.LOADING ||
            sessionState === LocalSessionState.HAS_LOCAL_SESSION) {
          setSessionState(LocalSessionState.AUTHENTICATED);
        }
      }
    } else if (sessionState !== LocalSessionState.NOT_LOADED &&
               sessionState !== LocalSessionState.LOADING &&
               sessionState !== LocalSessionState.NO_LOCAL_SESSION) {
      // Cognito auth became false while we had a session - token refresh likely failed
      console.warn('[AuthContext] Cognito authentication lost, signing out');
      setSessionState(LocalSessionState.NO_LOCAL_SESSION);
    }
  }, [cognito.authenticated, cognito.userSession, sessionState]);

  useEffect(() => {
    if (cognito.authenticated) {
      currentUserQuery.refetch();
    }
  }, [cognito.authenticated, currentUserQuery.refetch]);

  // Track the current SSE token to detect when it changes
  const sseTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!needsPlayer) {
      if (sse.current) {
        sse.current.close();
        sse.current = null;
        sseTokenRef.current = null;
      }
      return;
    }
    // Get token from cognitoSession
    const token = cognitoSession?.getIdToken()?.getJwtToken();

    // Check if token has changed - if so, close existing connection to recreate with new token
    if (token && sseTokenRef.current && token !== sseTokenRef.current && sse.current) {
      console.debug('[AuthContext] Token changed, recreating SSE connection');
      sse.current.close();
      sse.current = null;
    }

    // Only establish connection if we have a valid token
    if (token && !sse.current) {
      const sseUrl = `${GetGlobalSSEUrl()}?idToken=${token}`;
      sseTokenRef.current = token;

      try {
        console.debug('[AuthContext] Establishing SSE connection with token');
        sse.current = new ReconnectingEventSource(sseUrl);

        sse.current.onopen = () => {
          console.debug('[AuthContext] SSE connection opened');
        };

        sse.current.onerror = (error: any) => {
          console.debug('[AuthContext] SSE connection error:', error);

          // Check if it's a 403 Forbidden (auth failure)
          if (error?.status === 403 || error?.target?.status === 403) {
            console.error('[AuthContext] SSE 403 Forbidden - closing connection to prevent retries');
            if (sse.current) {
              sse.current.close();
              sse.current = null;
              sseTokenRef.current = null;
            }
          }
        };

        sse.current.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            processRealtimeEvent(data, cognito.user?.getUsername() || '');
          } catch (error) {
            console.error('[AuthContext] Error processing SSE message:', error);
          }
        };
      } catch (error) {
        console.error('[AuthContext] Error establishing SSE connection:', error);
      }
    } else if (!token && sse.current) {
      // Token is missing but connection exists - close it
      console.debug('[AuthContext] Token missing, closing SSE connection');
      sse.current.close();
      sse.current = null;
      sseTokenRef.current = null;
    }

    // Cleanup SSE connection on unmount or when token becomes invalid
    return () => {
      if (sse.current) {
        console.debug('[AuthContext] Cleaning up SSE connection');
        sse.current.close();
        sse.current = null;
        sseTokenRef.current = null;
      }
    };
  }, [cognitoSession, cognito.user, needsPlayer]);

  useEffect(() => {
    if (sessionState === LocalSessionState.NO_LOCAL_SESSION) {
      if (!isPublicRoute) {
        window.location.href = getSignInUrl(window.location);
      }
    }
  }, [sessionState, isPublicRoute]);

  // Provide context value
  const contextValue = {
    sessionState,
    loading,
    sessionHandler: sessionHandler || '',
    isExpiringSession,
    progressMessages,
    currentUser,
    currentUserStatus: currentUserQuery.status,
    currentUserLoading: currentUserQuery.isLoading,
    refetchCurrentUser: currentUserQuery.refetch,
    timezone,
    handleSignOut,
    addProgressMessage,
    setIsExpiringSession,
    getToken: () => cognitoSession?.getIdToken()?.getJwtToken(),
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <CustomNotificationCenter />
    </AuthContext.Provider>
  );
};

// Custom hook for using the auth context
export const useAuth = () => useContext(AuthContext);

export default AuthContext;
